import {ILowFareResult, LowFareStatusEnum} from "./low-fare-readers/low-fare-reader.interface";
import {IDayLowFareCache, ILowFaresCache} from "./low-fare.service.interface";
import {ILargeStorageConsumer} from "../storage/large-storage-consumer.interface";
import {IServiceFactory} from "../service-factory.interface";
import {runInAction} from "mobx";
import {TimeSpan} from "../../types/time-span";
import {RawSessionStorageKeys} from "../storage/raw-session-storage/raw-session-storage-keys";
import {LargeCacheHandler} from "../storage/large-cache.handler";
import {ILargeCacheHandlerEventSubscription} from "../storage/large-cache.handler.interface";

//Properties are defined as a single letter in order to take less space when they are saved in session storage
interface ICachedLowFareValue {
    s: LowFareStatusEnum; //status
    p?: { //price
        a: number; //amount
        c?: string; //currency
    }
}


interface LowFareHashCache {
    timestamp: number;
    dates: Record<string, ICachedLowFareValue>; //the key is the date
}

const CACHE_EXPIRATION_TIME = TimeSpan.fromMinutes(5);

export class LowFaresCache implements ILowFaresCache, ILargeStorageConsumer {
    constructor(private readonly services: IServiceFactory) {

        //the key of the cache is the hash of the low fare request
        this._largeCacheHandler = new LargeCacheHandler<LowFareHashCache, RawSessionStorageKeys>({
            name: 'low fare',
            services: this.services,
            maxItems: 50,
            cacheExpirationTime: CACHE_EXPIRATION_TIME,
            resetOnAuthorizationChanged: true,
            storage: this.services.rawSessionStorage,
            storageKey: RawSessionStorageKeys.FlightSearchLowFaresCache
        })

        this.services.largeStorageConsumersManager.registerSessionStorageConsumer(this);

    }

    private readonly _largeCacheHandler: LargeCacheHandler<LowFareHashCache, RawSessionStorageKeys>;

    clearUsedStorage(): void {
        this._largeCacheHandler.resetCache();
    }

    onCacheItemRemoved(callback: (hash: string) => void): ILargeCacheHandlerEventSubscription {
        return this._largeCacheHandler.onCacheItemRemoved(callback);
    }

    setCacheExpirationTime(hash: string, timeSpanFromNow: TimeSpan): void {
        const hashCache = this._largeCacheHandler.getItem(hash);
        if(!hashCache) {
            return;
        }

        runInAction(() => {
            hashCache.timestamp = Date.now() - CACHE_EXPIRATION_TIME.totalMilliseconds + timeSpanFromNow.totalMilliseconds
        });
    }

    isCacheExpired(hash: string): boolean {
        const hashCache = this._largeCacheHandler.getItem(hash);
        if(!hashCache) {
            return true;
        }
        return this._largeCacheHandler.isTimestampExpired(hashCache.timestamp);
    }

    getFromCache(hash: string, formattedDate: string): IDayLowFareCache | null {
        const cacheByHash = this._largeCacheHandler.getItem(hash);
        if(!cacheByHash) {
            return null;
        }

        const isCacheExpired = this._largeCacheHandler.isTimestampExpired(cacheByHash.timestamp);
        if(isCacheExpired) {
            return {
                isCacheExpired: isCacheExpired,
                dayLowFare: null}
        }

        const lowFare = cacheByHash.dates[formattedDate];
        if(!lowFare) {
            return {
                isCacheExpired: false,
                dayLowFare: null
            }
        }

        if(lowFare.p) { //if has price
            return {
                isCacheExpired: false,
                dayLowFare: {
                    status: lowFare.s,
                    price: this.services.currency.createPrice(lowFare.p.a, lowFare.p.c ?? this.services.currency.defaultCurrency)
                }
            };
        }

        return {
            isCacheExpired: false,
            dayLowFare: {
                status: lowFare.s,
            }
        };

    }

    putInCache(hash: string, formattedDate: string, lowFare: ILowFareResult): void {
        runInAction(() => {
            const timeStamp = Date.now();
            if(!this._largeCacheHandler.getItem(hash)) {
                this._largeCacheHandler.setItem(hash, {
                    timestamp: timeStamp,
                    dates: {}
                });
            }

            const hashCache = this._largeCacheHandler.getItem(hash)!;
            hashCache.timestamp = timeStamp;
            const cachedLowFare: ICachedLowFareValue = {
                s: lowFare.status
            }
            if(lowFare.price) {
                cachedLowFare.p = {
                    a: lowFare.price.amount
                }

                if(lowFare.price.currency !== this.services.currency.defaultCurrency) {
                    //In order to save some storage space we only write the currency if is different from default currency
                    cachedLowFare.p.c = lowFare.price.currency;
                }
            }

            hashCache.dates[formattedDate] = cachedLowFare;
        });

        this._largeCacheHandler.saveCache({saveAsync: true});
    }


}